import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Search, Eye, Check, X, Clock, AlertCircle, ChevronDown, Download, Star, Trophy, FileText, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, RefreshCw, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import ManualPostulationModal from '../components/ManualPostulationModal';

export default function ConsultaPostulaciones() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [postulaciones, setPostulaciones] = useState([]);
  const [convocatorias, setConvocatorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConvocatoria, setSelectedConvocatoria] = useState('');
  const [selectedEstado, setSelectedEstado] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [documentoSearch, setDocumentoSearch] = useState('');
  const [viewModal, setViewModal] = useState({ open: false, postulacion: null });
  const [estadoModal, setEstadoModal] = useState({ open: false, postulacion: null });
  const [calificacionModal, setCalificacionModal] = useState({ open: false, postulacion: null });
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [puntaje, setPuntaje] = useState('');
  const [observacionesCalificacion, setObservacionesCalificacion] = useState('');
  const [exporting, setExporting] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showManualPostModal, setShowManualPostModal] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConvocatorias();
  }, []);

  useEffect(() => {
    fetchAllPostulaciones();
  }, [currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchAllPostulaciones();
    }
  }, [selectedConvocatoria, selectedEstado, documentoSearch]);

  const fetchConvocatorias = async () => {
    try {
      const response = await fetch(`${API_URL}/api/convocatorias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConvocatorias(data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAllPostulaciones = async () => {
    try {
      setLoading(true);
      
      // Build query params for server-side filtering
      let url = `${API_URL}/api/postulaciones/todas?page=${currentPage}&limit=${itemsPerPage}`;
      
      if (selectedConvocatoria) url += `&convocatoria_id=${selectedConvocatoria}`;
      if (selectedEstado) url += `&estado=${selectedEstado}`;
      if (documentoSearch) url += `&cedula=${encodeURIComponent(documentoSearch)}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al cargar postulaciones');

      const data = await response.json();
      // Handle both old format (array) and new format (object with postulaciones)
      const postulacionesList = Array.isArray(data) ? data : (data.postulaciones || []);
      setPostulaciones(postulacionesList);
      setTotalItems(data.total || postulacionesList.length);
      setTotalPages(data.total_pages || Math.ceil((data.total || postulacionesList.length) / itemsPerPage));
    } catch (error) {
      toast.error('Error al cargar las postulaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchAllPostulaciones();
  };

  const handleUpdateEstado = async () => {
    if (!estadoModal.postulacion || !nuevoEstado) return;

    try {
      const response = await fetch(`${API_URL}/api/postulaciones/${estadoModal.postulacion.id}/estado?estado=${nuevoEstado}&observaciones=${encodeURIComponent(observaciones)}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al actualizar');

      toast.success('Estado actualizado correctamente');
      setEstadoModal({ open: false, postulacion: null });
      setNuevoEstado('');
      setObservaciones('');
      fetchAllPostulaciones();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateCalificacion = async () => {
    if (!calificacionModal.postulacion || puntaje === '') return;

    const puntajeNum = parseFloat(puntaje);
    if (isNaN(puntajeNum) || puntajeNum < 0 || puntajeNum > 100) {
      toast.error('El puntaje debe estar entre 0 y 100');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/postulaciones/${calificacionModal.postulacion.id}/calificacion`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          puntaje: puntajeNum,
          observaciones: observacionesCalificacion
        })
      });

      if (!response.ok) throw new Error('Error al calificar');

      toast.success('Calificación guardada');
      setCalificacionModal({ open: false, postulacion: null });
      setPuntaje('');
      setObservacionesCalificacion('');
      fetchAllPostulaciones();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleExport = async () => {
    if (!selectedConvocatoria) {
      toast.error('Seleccione una convocatoria para exportar');
      return;
    }

    setExporting(true);
    try {
      let url = `${API_URL}/api/postulaciones/export/${selectedConvocatoria}`;
      if (selectedEstado) {
        url += `?estado=${selectedEstado}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al exportar');

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const filename = `beneficiarios_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      // Force download
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);

      toast.success('Archivo descargado: ' + filename);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadZip = async (idsOverride) => {
    const ids = idsOverride && idsOverride.length ? idsOverride : Array.from(selectedIds);
    if (!ids.length) {
      toast.error('Selecciona al menos una postulación');
      return;
    }
    setDownloadingZip(true);
    try {
      const response = await fetch(`${API_URL}/api/postulaciones/download-attachments-zip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ postulacion_ids: ids }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al generar el ZIP');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const filename = `adjuntos_postulaciones_${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)}.zip`;
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);
      toast.success(`ZIP descargado (${ids.length} postulación(es))`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDownloadingZip(false);
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (checked, rows) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      rows.forEach((r) => (checked ? next.add(r.id) : next.delete(r.id)));
      return next;
    });
  };

  // Client-side filtering only for name/email search (server handles the rest)
  const filteredPostulaciones = postulaciones.filter(p => {
    const matchesSearch = !searchTerm || 
      p.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.user_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Sort by puntaje if available
  const sortedPostulaciones = [...filteredPostulaciones].sort((a, b) => {
    if (a.puntaje !== undefined && b.puntaje !== undefined) {
      return b.puntaje - a.puntaje;
    }
    return 0;
  });

  // Pagination helpers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  const getEstadoInfo = (estado) => {
    const estados = {
      pendiente: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      en_revision: { label: 'En Revisión', color: 'bg-blue-100 text-blue-800', icon: AlertCircle },
      aprobada: { label: 'Aprobada', color: 'bg-green-100 text-green-800', icon: Check },
      rechazada: { label: 'Rechazada', color: 'bg-red-100 text-red-800', icon: X }
    };
    return estados[estado] || { label: estado, color: 'bg-gray-100 text-gray-800', icon: Clock };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getRankingBadge = (puntaje, index) => {
    if (puntaje === undefined) return null;
    if (index === 0) return <Trophy className="h-5 w-5 text-yellow-500" />;
    if (index === 1) return <Trophy className="h-5 w-5 text-gray-400" />;
    if (index === 2) return <Trophy className="h-5 w-5 text-amber-600" />;
    return <span className="text-xs text-slate-500">#{index + 1}</span>;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#0F172A] border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_login-verify-system/artifacts/gyieqwft_Captura%20de%20Pantalla%202025-12-10%20a%20la%28s%29%207.03.41%20a.%C2%A0m..png" 
              alt="Logo" 
              className="h-12 w-auto object-contain brightness-0 invert"
            />
            <div className="border-l border-slate-600 pl-4">
              <h1 className="text-xl font-semibold text-white">Consulta de Postulaciones</h1>
            </div>
          </div>
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-slate-300 hover:text-white">
            <ArrowLeft className="h-5 w-5" />
            <span>Volver</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por nombre, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg"
                data-testid="search-name-email"
              />
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por cedula..."
                value={documentoSearch}
                onChange={(e) => setDocumentoSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg"
                data-testid="search-cedula"
              />
            </div>
            <select
              value={selectedConvocatoria}
              onChange={(e) => setSelectedConvocatoria(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg"
              data-testid="filter-convocatoria"
            >
              <option value="">Todas las convocatorias</option>
              {convocatorias.map(c => (
                <option key={c.id} value={c.id}>{c.codigo} - {c.nombre?.substring(0, 30)}...</option>
              ))}
            </select>
            <select
              value={selectedEstado}
              onChange={(e) => setSelectedEstado(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg"
              data-testid="filter-estado"
            >
              <option value="">Todos los estados</option>
              <option value="pendiente">Pendiente</option>
              <option value="en_revision">En Revision</option>
              <option value="aprobada">Aprobada</option>
              <option value="rechazada">Rechazada</option>
            </select>
            <button
              onClick={handleSearch}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              data-testid="btn-search"
            >
              <RefreshCw className="h-4 w-4" />
              Buscar
            </button>
            <button
              onClick={handleExport}
              disabled={exporting || !selectedConvocatoria}
              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                selectedConvocatoria 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-slate-200 text-slate-500 cursor-not-allowed'
              }`}
              data-testid="btn-export"
            >
              <Download className="h-4 w-4" />
              {exporting ? 'Exportando...' : 'Exportar Excel'}
            </button>
            <button
              onClick={() => handleDownloadZip()}
              disabled={downloadingZip || selectedIds.size === 0}
              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                selectedIds.size > 0
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-slate-200 text-slate-500 cursor-not-allowed'
              }`}
              data-testid="btn-download-zip"
              title="Descargar los adjuntos del formulario de las postulaciones seleccionadas"
            >
              <FileText className="h-4 w-4" />
              {downloadingZip
                ? 'Generando ZIP...'
                : `Descargar carpeta (${selectedIds.size})`}
            </button>
            <button
              onClick={() => setShowManualPostModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              data-testid="manual-postulation-btn"
            >
              <UserPlus className="h-4 w-4" />
              Postular Manualmente
            </button>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
            <div className="text-sm text-slate-600">
              Mostrando <span className="font-semibold">{sortedPostulaciones.length}</span> de <span className="font-semibold">{totalItems}</span> postulaciones
              {selectedConvocatoria && <span className="ml-2 text-blue-600">(filtrado por convocatoria)</span>}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Por pagina:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-2 py-1 border border-slate-300 rounded text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-blue-500" />
              Cargando...
            </div>
          ) : sortedPostulaciones.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No se encontraron postulaciones</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#0F172A] text-white">
                  <tr>
                    <th className="px-3 py-3 text-center text-sm font-semibold w-10">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded"
                        data-testid="select-all-checkbox"
                        checked={
                          sortedPostulaciones.length > 0 &&
                          sortedPostulaciones.every((r) => selectedIds.has(r.id))
                        }
                        onChange={(e) => toggleSelectAll(e.target.checked, sortedPostulaciones)}
                      />
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-semibold">Pos.</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold">Convocatoria</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold">Postulante</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold">Email</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold">Cedula</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold">Fecha</th>
                    <th className="px-3 py-3 text-center text-sm font-semibold">Puntaje</th>
                    <th className="px-3 py-3 text-center text-sm font-semibold">Estado</th>
                    <th className="px-3 py-3 text-center text-sm font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {sortedPostulaciones.map((postulacion, index) => {
                    const estadoInfo = getEstadoInfo(postulacion.estado);
                    const EstadoIcon = estadoInfo.icon;
                    const globalIndex = (currentPage - 1) * itemsPerPage + index;
                    return (
                      <tr key={postulacion.id} className="hover:bg-slate-50">
                        <td className="px-3 py-3 text-center">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded"
                            data-testid={`row-checkbox-${postulacion.id}`}
                            checked={selectedIds.has(postulacion.id)}
                            onChange={() => toggleSelected(postulacion.id)}
                          />
                        </td>
                        <td className="px-3 py-3 text-center">
                          {getRankingBadge(postulacion.puntaje, globalIndex)}
                        </td>
                        <td className="px-3 py-3 text-sm">
                          <div className="font-medium text-slate-900 max-w-[200px] truncate">{postulacion.convocatoria_nombre}</div>
                          <div className="text-xs text-slate-500">{postulacion.convocatoria_codigo}</div>
                        </td>
                        <td className="px-3 py-3 text-sm text-slate-900 font-medium">{postulacion.user_name || '-'}</td>
                        <td className="px-3 py-3 text-sm text-slate-600">{postulacion.user_email}</td>
                        <td className="px-3 py-3 text-sm text-slate-600 font-mono">{postulacion.user_document || '-'}</td>
                        <td className="px-3 py-3 text-sm text-slate-600">{formatDate(postulacion.fecha_postulacion)}</td>
                        <td className="px-3 py-3 text-center">
                          {postulacion.puntaje !== undefined ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-sm font-bold rounded ${
                              postulacion.puntaje >= 80 ? 'bg-green-100 text-green-800' :
                              postulacion.puntaje >= 60 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {postulacion.puntaje}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-sm">Sin calificar</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${estadoInfo.color}`}>
                            <EstadoIcon className="h-3 w-3" />
                            {estadoInfo.label}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button 
                              onClick={() => setViewModal({ open: true, postulacion })} 
                              className="p-1 text-slate-500 hover:text-blue-600"
                              title="Ver detalles"
                            >
                              <Eye className="h-5 w-5" />
                            </button>
                            <button 
                              onClick={() => {
                                setCalificacionModal({ open: true, postulacion });
                                setPuntaje(postulacion.puntaje?.toString() || '');
                                setObservacionesCalificacion(postulacion.observaciones_calificacion || '');
                              }} 
                              className="p-1 text-slate-500 hover:text-yellow-600"
                              title="Calificar"
                            >
                              <Star className="h-5 w-5" />
                            </button>
                            <button 
                              onClick={() => {
                                setEstadoModal({ open: true, postulacion });
                                setNuevoEstado(postulacion.estado);
                                setObservaciones(postulacion.observaciones || '');
                              }} 
                              className="p-1 text-slate-500 hover:text-green-600"
                              title="Cambiar estado"
                            >
                              <ChevronDown className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination Controls */}
          {!loading && totalPages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="text-sm text-slate-600">
                Pagina {currentPage} de {totalPages}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Primera pagina"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Pagina anterior"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                
                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      page === currentPage
                        ? 'bg-blue-600 text-white'
                        : 'hover:bg-slate-200 text-slate-700'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Pagina siguiente"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Ultima pagina"
                >
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* View Modal */}
      {viewModal.open && viewModal.postulacion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Detalles de la Postulación</h3>
              <button onClick={() => setViewModal({ open: false, postulacion: null })} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-slate-500">Convocatoria</p><p className="font-medium">{viewModal.postulacion.convocatoria_nombre}</p></div>
                <div><p className="text-xs text-slate-500">Código</p><p className="font-medium">{viewModal.postulacion.convocatoria_codigo}</p></div>
                <div><p className="text-xs text-slate-500">Postulante</p><p className="font-medium">{viewModal.postulacion.user_name}</p></div>
                <div><p className="text-xs text-slate-500">Email</p><p className="font-medium">{viewModal.postulacion.user_email}</p></div>
                <div><p className="text-xs text-slate-500">Cédula/Documento</p><p className="font-medium font-mono">{viewModal.postulacion.user_document || '-'}</p></div>
                <div><p className="text-xs text-slate-500">Fecha Postulación</p><p className="font-medium">{formatDate(viewModal.postulacion.fecha_postulacion)}</p></div>
                <div><p className="text-xs text-slate-500">Estado</p><p className="font-medium">{getEstadoInfo(viewModal.postulacion.estado).label}</p></div>
                <div><p className="text-xs text-slate-500">Puntaje</p><p className="font-medium text-lg">{viewModal.postulacion.puntaje ?? 'Sin calificar'}</p></div>
              </div>
              {viewModal.postulacion.observaciones && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Observaciones de Estado</p>
                  <p className="text-sm">{viewModal.postulacion.observaciones}</p>
                </div>
              )}
              {viewModal.postulacion.observaciones_calificacion && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Observaciones de Calificación</p>
                  <p className="text-sm">{viewModal.postulacion.observaciones_calificacion}</p>
                </div>
              )}
              
              {/* Respuestas del Formulario de Postulación */}
              {viewModal.postulacion.respuestas_formulario && Object.keys(viewModal.postulacion.respuestas_formulario).length > 0 && (
                <div className="mt-6 border-t pt-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Respuestas del Formulario de Postulación
                  </h4>
                  <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                    {Object.entries(viewModal.postulacion.respuestas_formulario).map(([campoId, respuesta], idx) => {
                      // Find the field name from the convocatoria's form fields
                      const convocatoria = convocatorias.find(c => c.id === viewModal.postulacion.convocatoria_id);
                      const campo = convocatoria?.formulario_campos?.find(f => String(f.id) === String(campoId));
                      const nombreCampo = campo?.nombre || campoId.replace(/_/g, ' ');
                      
                      return (
                        <div key={idx} className="border-b border-blue-100 pb-2 last:border-0 last:pb-0">
                          <p className="text-xs text-blue-600 font-medium">
                            {nombreCampo}
                          </p>
                          <p className="text-sm text-slate-800 mt-1">
                            {typeof respuesta === 'boolean' 
                              ? (respuesta ? '✓ Sí' : '✗ No')
                              : (respuesta || '-')}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Estado Modal */}
      {estadoModal.open && estadoModal.postulacion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Actualizar Estado</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nuevo Estado</label>
                <select value={nuevoEstado} onChange={(e) => setNuevoEstado(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                  <option value="pendiente">Pendiente</option>
                  <option value="en_revision">En Revisión</option>
                  <option value="aprobada">Aprobada</option>
                  <option value="rechazada">Rechazada</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Observaciones</label>
                <textarea value={observaciones} onChange={(e) => setObservaciones(e.target.value)} rows={3} className="w-full px-3 py-2 border rounded-lg" placeholder="Agregar comentarios..." />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEstadoModal({ open: false, postulacion: null })} className="px-4 py-2 border rounded-lg hover:bg-slate-50">Cancelar</button>
              <button onClick={handleUpdateEstado} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Calificacion Modal */}
      {calificacionModal.open && calificacionModal.postulacion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-500" />
              Calificar Postulación
            </h3>
            <div className="mb-4 p-3 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium">{calificacionModal.postulacion.user_name}</p>
              <p className="text-xs text-slate-500">{calificacionModal.postulacion.user_email}</p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Puntaje (0-100) <span className="text-red-500">*</span>
                </label>
                <input 
                  type="number" 
                  min="0" 
                  max="100" 
                  step="0.1"
                  value={puntaje} 
                  onChange={(e) => setPuntaje(e.target.value)} 
                  className="w-full px-3 py-2 border rounded-lg text-2xl font-bold text-center" 
                  placeholder="0-100"
                />
                <div className="flex justify-between mt-2 text-xs text-slate-500">
                  <span>0 - Mínimo</span>
                  <span className={`font-medium ${
                    parseFloat(puntaje) >= 80 ? 'text-green-600' :
                    parseFloat(puntaje) >= 60 ? 'text-yellow-600' :
                    parseFloat(puntaje) > 0 ? 'text-red-600' : ''
                  }`}>
                    {parseFloat(puntaje) >= 80 ? 'Excelente' :
                     parseFloat(puntaje) >= 60 ? 'Bueno' :
                     parseFloat(puntaje) > 0 ? 'Bajo' : ''}
                  </span>
                  <span>100 - Máximo</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Observaciones de Calificación</label>
                <textarea 
                  value={observacionesCalificacion} 
                  onChange={(e) => setObservacionesCalificacion(e.target.value)} 
                  rows={3} 
                  className="w-full px-3 py-2 border rounded-lg" 
                  placeholder="Justificación del puntaje asignado..." 
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setCalificacionModal({ open: false, postulacion: null })} className="px-4 py-2 border rounded-lg hover:bg-slate-50">Cancelar</button>
              <button onClick={handleUpdateCalificacion} className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600">
                Guardar Calificación
              </button>
            </div>
          </div>
        </div>
      )}

      <ManualPostulationModal
        open={showManualPostModal}
        onClose={() => setShowManualPostModal(false)}
        token={token}
        convocatorias={convocatorias}
        onSuccess={() => fetchAllPostulaciones()}
      />
    </div>
  );
}
